// FILECONTENT_START src/common/components/Inputs/TimePicker/TimePicker.tsx
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import {
  alpha,
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from "@mui/material";
import Popper from "@mui/material/Popper";
import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
//@ts-ignore
import tgrx from "@tgrx/getcontrasttext";
import moment from "moment-timezone";
import { IMask, IMaskInput } from "react-imask";
import stc from "string-to-color";
import { BaseInputProps } from "../types";
import ScrollPicker from "./ScrollPicker";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCustom = forwardRef<HTMLElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const momentFormat = props.name === "is24Hours" ? "HH:mmm" : "hh:mm aa";
    const { onChange, ...other } = props;
    const am = moment("08:00", "HH:mm").format("A");
    const pm = moment("16:00", "HH:mm").format("A");

    return (
      <IMaskInput
        {...other}
        mask={Date}
        pattern={momentFormat}
        autofix
        blocks={
          props.name === "is24Hours"
            ? {
                HH: {
                  mask: IMask.MaskedRange,
                  from: 0,
                  to: 23,
                },
                mm: {
                  mask: IMask.MaskedRange,
                  from: 0,
                  to: 59,
                },
              }
            : {
                hh: {
                  mask: IMask.MaskedRange,
                  from: 0,
                  to: 12,
                },
                mm: {
                  mask: IMask.MaskedRange,
                  from: 0,
                  to: 59,
                },
                aa: {
                  mask: IMask.MaskedEnum,
                  //@ts-ignore
                  enum: [
                    `${am.toUpperCase()} `,
                    `${pm.toUpperCase()} `,
                    `${am.toLowerCase()} `,
                    `${pm.toLowerCase()} `,
                  ],
                },
              }
        }
        inputRef={ref as any}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

export type Option = {
  label: string;
  value: any;
};

const hours12: Option[] = [
  { label: "01", value: "01" },
  { label: "02", value: "02" },
  { label: "03", value: "03" },
  { label: "04", value: "04" },
  { label: "05", value: "05" },
  { label: "06", value: "06" },
  { label: "07", value: "07" },
  { label: "08", value: "08" },
  { label: "09", value: "09" },
  { label: "10", value: "10" },
  { label: "11", value: "11" },
  { label: "12", value: "12" },
];
const hours24: Option[] = [
  { label: "00", value: "00" },
  { label: "01", value: "01" },
  { label: "02", value: "02" },
  { label: "03", value: "03" },
  { label: "04", value: "04" },
  { label: "05", value: "05" },
  { label: "06", value: "06" },
  { label: "07", value: "07" },
  { label: "08", value: "08" },
  { label: "09", value: "09" },
  { label: "10", value: "10" },
  { label: "11", value: "11" },
  { label: "12", value: "12" },
  { label: "13", value: "13" },
  { label: "14", value: "14" },
  { label: "15", value: "15" },
  { label: "16", value: "16" },
  { label: "17", value: "17" },
  { label: "18", value: "18" },
  { label: "19", value: "19" },
  { label: "20", value: "20" },
  { label: "21", value: "21" },
  { label: "22", value: "22" },
  { label: "23", value: "23" },
];

function getMinutes(increment: number): Option[] {
  let minute = 0;
  let options: Option[] = [];
  if (!increment) {
    return options;
  }
  while (minute <= 59) {
    let value = String(minute).padStart(2, "0");
    options.push({
      label: value,
      value,
    });
    minute += increment;
  }
  return options;
}
export interface ColorIndicatorProps {
  /**
   * Show vertical line at left of the field inside. color string or "auto" generate unique color by props.id
   */
  indicatorColor?: "auto" | string;
  /**
   * It works only with indicatorColor="auto"
   */
  indicatorOpacity?: number;
}

export function getContrastColor(
  color?: string,
  opacity: number = 1,
  contrastThreshold: number = 3
): string {
  if (!color) {
    return "#000000";
  }

  return tgrx.default({
    background: color,
    contrastThreshold: contrastThreshold / opacity,
    warnings: false,
  });
}

export function getColorsFromString(
  str?: string,
  opacity?: number
): {
  color?: string;
  backgroundColor?: string;
} {
  if (!str) {
    return {};
  }
  let backgroundColor = opacity ? alpha(stc(str), opacity) : stc(str);
  const color = getContrastColor(backgroundColor, opacity);
  return { color, backgroundColor };
}

export function getTextFieldColorIndicatorSx(
  id: any,
  { indicatorColor, indicatorOpacity }: ColorIndicatorProps
) {
  let sx = {};
  if (indicatorColor === "auto") {
    sx = {
      "& .MuiFilledInput-root": {
        boxShadow: `inset 5px 0 0 0 ${
          getColorsFromString(id, indicatorOpacity || 1).backgroundColor
        }`,
      },
    };
  } else if (indicatorColor) {
    sx = {
      "& .MuiFilledInput-root": {
        boxShadow: `inset 5px 0 0 0 ${indicatorColor}`,
      },
    };
  }
  return sx;
}

export type TimePickerState = {
  hour: string;
  minute: string;
  ampm: string;
  date: string;
  open: boolean;
  value: string;
};

export type TimePickerProps = {
  is24Hours?: boolean;
  minuteIncrement?: number;
  shorcutIncrement?: number;
  changeOnAccept?: boolean;
  changeOnClickAway?: boolean;
  extraAdornment?: ReactNode;
  dates?: string[];
  dateFormat?: string;
  renderBottomContent?: (state: TimePickerState) => ReactNode;
  renderInput?: Function;
  ignoreChangesWhenOpen?: boolean;
  //Depricated
  minutesStep?: boolean;
  forceDateTime?: boolean;
} & TextFieldProps &
  BaseInputProps;

export default function TimePicker({
  id,
  error,
  helperText,
  clearable = true,
  indicatorColor,
  indicatorOpacity,
  value,
  onChange,
  isDirty,
  onBlur,
  label,
  minuteIncrement = 1,
  shorcutIncrement = 15,
  changeOnAccept = true,
  changeOnClickAway = true,
  required,
  is24Hours = false,
  ignoreChangesWhenOpen,
  //Depricated
  minutesStep,
  extraAdornment,
  dates,
  dateFormat = "MM/DD",
  renderInput,
  renderBottomContent,
  forceDateTime,
  ...textFieldProps
}: TimePickerProps) {
  const amorpm = useRef<Option[]>([
    {
      label: moment("08:00", "HH:mm").format("A"),
      value: moment("08:00", "HH:mm").format("A"),
    },
    {
      label: moment("16:00", "HH:mm").format("A"),
      value: moment("16:00", "HH:mm").format("A"),
    },
  ]);

  const dateOptions: Option[] = dates
    ? dates.map(d => ({
        value: d,
        label: moment(d).format(dateFormat),
      }))
    : [];

  const defaultTimePickerState = useRef<TimePickerState>({
    hour: "01",
    minute: "00",
    ampm: moment("08:00", "HH:mm").format("A"),
    open: false,
    ...parseValues(),
    value: parseValue(value as any) || "",
  });

  const lastState = useRef<TimePickerState>(defaultTimePickerState.current);
  const [open, setOpen] = useState<boolean>(false);
  const [state, _setState] = useState<TimePickerState>(
    defaultTimePickerState.current
  );

  const setState = useCallback(
    (_state: Partial<TimePickerState>) => {
      let newState = Object.assign({}, lastState.current, _state);
      lastState.current = newState;
      return _setState({ ...newState });
    },
    [state]
  );

  //const [open, setOpen] = useState<boolean>(false);
  const minutes = getMinutes(minuteIncrement);
  const shortcuts = getMinutes(shorcutIncrement);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isInternalChange = useRef<boolean>(false);
  const textFieldRef = useRef<null | HTMLDivElement>(null);
  const theme = useTheme();

  const wrapperHeight = 140;
  const showModal = () => {
    setState({ open: true });
  };

  const hideModal = () => {
    setState({ ...defaultTimePickerState.current });
  };

  function getValue() {
    if (dates || forceDateTime) {
      const datestr = is24Hours
        ? `${state.date} ${state.hour}:${state.minute}`
        : `${state.date} ${state.hour}:${state.minute} ${state.ampm}`;
      const d = is24Hours
        ? moment(datestr, "YYYY-MM-DD HH:mm")
        : moment(datestr, "YYYY-MM-DD hh:mm A");

      if (d.isValid()) {
        return d.format("YYYY-MM-DD HH:mm");
      }
    } else {
      const datestr = `${state.hour}:${state.minute} ${state.ampm}`;
      const d = is24Hours
        ? moment(datestr, "HH:mm")
        : moment(datestr, "hh:mm A");

      if (d.isValid()) {
        return d.format("HH:mm");
      }
    }
    return null;
  }

  function parseValue(datestr?: string) {
    let d: any = null;
    if (dates || forceDateTime) {
      d = moment(value);
    } else {
      d = moment(value, "HH:mm");
    }

    if (d.isValid()) {
      if (is24Hours) {
        return d.format("HH:mm");
      } else {
        return d.format("hh:mm A");
      }
    }
    return "";
  }

  function parseValues() {
    let d: any = null;
    if (dates || forceDateTime) {
      d = moment(value);
    } else {
      d = moment(value, "HH:mm");
    }

    if (d.isValid()) {
      return {
        date: d.format("YYYY-MM-DD"),
        hour: d.format("hh"),
        minute: d.format("mm"),
        ampm: d.format("A"),
      } as any;
    }
    return {} as any;

    /*const d = moment(value as any, "HH:mm");
    if (d.isValid()) {
      return {
        hour: is24Hours ? d.format("HH") : d.format("hh"),
        minute: d.format("mm"),
        ampm: d.format("A"),
      };
    }
    return {};*/
  }

  const handleAccept = () => {
    onChange && onChange({ target: { value: getValue() } } as any);
    hideModal();
  };

  const handleInputValueChange = () => {
    let parsedValue = null;
    if (is24Hours) {
      parsedValue = state.value ? moment(state.value, "HH:mm") : null;
    } else {
      parsedValue = state.value ? moment(state.value, "hh:mm A") : null;
    }
    if (state.value === "") {
      onChange && onChange({ target: { value: null } } as any);
    } else if (parsedValue && parsedValue.isValid()) {
      let exportedValue = parsedValue.format("HH:mm");
      if (exportedValue === state.value) {
        onChange && onChange({ target: { value: exportedValue } } as any);
      }
    }
  };

  /*const inputRef = useRef();*/

  const _label = required ? (
    <>
      {label} <b style={{ color: "red" }}> *</b>
    </>
  ) : (
    label
  );

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      handleInputValueChange();
    }
  }, [state.value]);

  useEffect(() => {
    if (state.open) {
      setTimeout(() => {
        setOpen(true);
      }, 400);
    } else {
      setOpen(false);
    }
  }, [state.open]);

  useEffect(() => {
    isInternalChange.current = false;
    if (!ignoreChangesWhenOpen || !open) {
      setState({ value: parseValue(value as any), ...parseValues() });
    }
  }, [value, state.open]);

  useEffect(() => {
    if (!changeOnAccept) {
      isInternalChange.current = false;
      onChange && onChange({ target: { value: getValue() } } as any);
    }
  }, [state.hour, state.minute, state.ampm]);
  return (
    <Box>
      {!!renderInput ? (
        <Box
          ref={textFieldRef}
          onClick={
            textFieldProps.disabled
              ? undefined
              : evt => {
                  evt.stopPropagation();
                  //handleClick()
                  //onChange && onChange({ target: { value: null } } as any);
                  if (state.open) {
                    if (changeOnClickAway) {
                      handleAccept();
                    } else {
                      hideModal();
                    }
                  } else {
                    showModal();
                  }
                }
          }
        >
          {renderInput({ value, ...textFieldProps })}
        </Box>
      ) : (
        <TextField
          ref={textFieldRef}
          label={_label}
          sx={{
            ...getTextFieldColorIndicatorSx(id, {
              indicatorColor,
              indicatorOpacity,
            }),
            ...textFieldProps.sx,
          }}
          id={id}
          value={state.value}
          helperText={error || helperText}
          error={error ? true : false}
          onChange={evt => {
            if (state.value !== evt.target.value) {
              isInternalChange.current = true;
              setState({ value: evt.target.value });
            }
          }}
          onBlur={evt => {
            //hideModal();
            onBlur && onBlur(evt);
          }}
          /*onFocus={evt => {
          evt.stopPropagation();
          showModal();
          //onBlur && onBlur(evt);
        }}*/
          InputProps={{
            readOnly: true,
            inputComponent: TextMaskCustom as any,
            name: is24Hours ? "is24Hours" : undefined,
            endAdornment: (
              <InputAdornment position="end" style={{ marginRight: -6 }}>
                {!!state.value && state.value !== "" && clearable && (
                  <IconButton
                    disabled={textFieldProps.disabled}
                    onClick={() => {
                      isInternalChange.current = true;

                      onChange && onChange({ target: { value: null } } as any);
                      //setState({ ...defaultTimePickerState.current });
                    }}
                    sx={{ color: theme.palette.text.secondary }}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  disabled={textFieldProps.disabled}
                  //onClick={() => setOpen(true)}
                  onClick={evt => {
                    evt.stopPropagation();
                    //handleClick()
                    //onChange && onChange({ target: { value: null } } as any);
                    if (state.open) {
                      if (changeOnClickAway) {
                        handleAccept();
                      } else {
                        hideModal();
                      }
                    } else {
                      showModal();
                    }
                  }}
                  size="small"
                >
                  <AccessTimeIcon fontSize="small" />
                </IconButton>
                {extraAdornment}
              </InputAdornment>
            ),
          }}
          {...textFieldProps}
        />
      )}
      <Popper
        id={id}
        open={state.open}
        anchorEl={textFieldRef.current}
        transition
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper
              elevation={5}
              sx={{
                p: 1,
              }}
            >
              <ClickAwayListener
                onClickAway={() => {
                  if (changeOnClickAway) {
                    handleAccept();
                  } else {
                    state.open && hideModal();
                  }
                }}
              >
                <Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: "stretch",
                      //backgroundColor: "red",
                    }}
                  >
                    <Typography flex={1} textAlign="center" variant="caption">
                      Hours
                    </Typography>
                    <Typography flex={1} textAlign="center" variant="caption">
                      Minutes
                    </Typography>
                    {!is24Hours && (
                      <Typography flex={1} textAlign="center" variant="caption">
                        AM/PM
                      </Typography>
                    )}
                    {!!dates && (
                      <Typography flex={1} textAlign="center" variant="caption">
                        Date
                      </Typography>
                    )}
                  </Stack>
                  <Box
                    sx={{
                      p: 2,
                      paddingBottom: 0.5,
                      paddingTop: 0.5,
                      position: "relative",
                      border: theme => `solid 1px ${theme.palette.divider}`,
                      borderRadius: "4px",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      divider={<Divider orientation="vertical" flexItem />}
                      sx={{
                        position: "relative",
                        height: wrapperHeight,
                        alignItems: "stretch",
                        //backgroundColor: "red",
                      }}
                    >
                      <ScrollPicker
                        useAnimation={open}
                        options={is24Hours ? hours24 : hours12}
                        value={state.hour}
                        onValueChange={value => {
                          setState({ hour: value as string });
                        }}
                        wrapperHeight={wrapperHeight}
                      />
                      <ScrollPicker
                        useAnimation={open}
                        options={minutes}
                        value={state.minute}
                        onValueChange={value => {
                          setState({ minute: value as string });
                        }}
                        wrapperHeight={wrapperHeight}
                      />
                      {!is24Hours && (
                        <ScrollPicker
                          useAnimation={open}
                          options={amorpm.current}
                          value={state.ampm}
                          onValueChange={value => {
                            setState({ ampm: value as string });
                          }}
                          wrapperHeight={wrapperHeight}
                        />
                      )}
                      {!!dates && (
                        <ScrollPicker
                          itemWidth={44}
                          options={dateOptions}
                          value={state.date}
                          onValueChange={value => {
                            setState({ date: value as string });
                          }}
                          wrapperHeight={wrapperHeight}
                        />
                      )}
                    </Stack>
                    <Box
                      sx={{
                        position: "absolute",
                        left: theme => theme.spacing(1),
                        right: theme => theme.spacing(1),
                        top: "50%",
                        borderRadius: "40px",
                        marginTop: `${-4 - 30 / 2}px`,
                        height: `${30}px`,
                        borderBottom: theme =>
                          `dashed 1px ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )}`,
                        borderTop: theme =>
                          `dashed 1px ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )}`,
                        backgroundColor: theme =>
                          alpha(theme.palette.text.primary, 0.1),
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                  {!!renderBottomContent ? renderBottomContent(state) : null}
                  {shortcuts.length > 0 && (
                    <Box sx={{ p: 2 }}>
                      <ButtonGroup variant="outlined" size="small">
                        {shortcuts.map(option => (
                          <Button
                            key={option.value}
                            onClick={() => setState({ minute: option.value })}
                            variant={
                              option.value === state.minute
                                ? "contained"
                                : undefined
                            }
                          >
                            {state.hour}: {option.label}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Stack direction="row" spacing={2}>
                      <Button variant="outlined" onClick={() => hideModal()}>
                        Cancel
                      </Button>
                      <Button onClick={() => handleAccept()}>Ok</Button>
                    </Stack>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
}

// FILECONTENT_END src/common/components/Inputs/TimePicker/TimePicker.tsx